import { lazy, Suspense, useEffect, useState } from "react";
import type React from "react";
import { Bot, Filter, History, KeyRound, LogOut, Mail, MapPin, Play, Send, Settings, Shield, Users, X } from "lucide-react";
import type { SessionUser } from "../shared/types.js";
import { product } from "../shared/product.js";
import { api } from "./api.js";
import type { HelpMessage, OutreachHandoff, Tab } from "./types.js";
import { Field, FullScreen } from "./ui.js";

const Dashboard = lazy(() => import("./Dashboard.js").then((module) => ({ default: module.Dashboard })));
const Leads = lazy(() => import("./Leads.js").then((module) => ({ default: module.Leads })));
const OutreachPage = lazy(() => import("./OutreachPage.js").then((module) => ({ default: module.OutreachPage })));
const Crawls = lazy(() => import("./Crawls.js").then((module) => ({ default: module.Crawls })));
const SettingsPage = lazy(() => import("./SettingsPage.js").then((module) => ({ default: module.SettingsPage })));
const UsersPage = lazy(() => import("./AdminPages.js").then((module) => ({ default: module.UsersPage })));
const AuditPage = lazy(() => import("./AdminPages.js").then((module) => ({ default: module.AuditPage })));
export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [bootstrapped, setBootstrapped] = useState<boolean | null>(null);

  useEffect(() => {
    api.get<{ hasUsers: boolean }>("/api/auth/status")
      .then((status) => setBootstrapped(status.hasUsers))
      .catch(() => setBootstrapped(false));
    api.get<{ user: SessionUser }>("/api/auth/me")
      .then((result) => setUser(result.user))
      .catch(() => undefined);
  }, []);

  if (bootstrapped === null) return <FullScreen title="Lade Lead Intelligence..." />;
  if (!bootstrapped) return <Bootstrap onDone={() => setBootstrapped(true)} />;
  if (!user) return <Login onLogin={setUser} />;
  return <Shell user={user} onLogout={() => setUser(null)} />;
}

function Bootstrap({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", bootstrapToken: "" });
  const [error, setError] = useState("");
  return (
    <AuthPanel title="Ersten Admin anlegen" subtitle="Dieser Schritt ist nur möglich, solange noch kein Nutzer existiert.">
      <Field label="Name" value={form.name} onChange={(name) => setForm({ ...form, name })} />
      <Field label="E-Mail" value={form.email} onChange={(email) => setForm({ ...form, email })} />
      <Field label="Passwort" type="password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
      <Field label="Bootstrap Token" type="password" value={form.bootstrapToken} onChange={(bootstrapToken) => setForm({ ...form, bootstrapToken })} />
      {error && <p className="error">{error}</p>}
      <button
        className="primary"
        onClick={() =>
          api.post("/api/auth/bootstrap", form).then(onDone).catch((err) => setError(err.message))
        }
      >
        <Shield size={18} /> Admin erstellen
      </button>
    </AuthPanel>
  );
}

function Login({ onLogin }: { onLogin: (user: SessionUser) => void }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  return (
    <AuthPanel title={product.name} subtitle={product.description}>
      <Field label="E-Mail" value={form.email} onChange={(email) => setForm({ ...form, email })} />
      <Field label="Passwort" type="password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
      {error && <p className="error">{error}</p>}
      <button className="primary" onClick={() => api.post<{ user: SessionUser }>("/api/auth/login", form).then((r) => onLogin(r.user)).catch((err) => setError(err.message))}>
        <KeyRound size={18} /> Einloggen
      </button>
    </AuthPanel>
  );
}

function AuthPanel({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <h1>{title}</h1>
        <p>{subtitle}</p>
        <div className="stack">{children}</div>
      </section>
    </main>
  );
}

function Shell({ user, onLogout }: { user: SessionUser; onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>("dashboard");
  const [outreachHandoff, setOutreachHandoff] = useState<OutreachHandoff | null>(null);
  const tabs: Array<[Tab, React.ReactNode, string, Array<SessionUser["role"]>]> = [
    ["dashboard", <MapPin size={18} />, "Übersicht", ["admin", "sales", "viewer"]],
    ["leads", <Filter size={18} />, "Leadlisten", ["admin", "sales", "viewer"]],
    ["outreach", <Mail size={18} />, "Outreach", ["admin", "sales"]],
    ["crawl", <Play size={18} />, "Crawls", ["admin", "sales"]],
    ["settings", <Settings size={18} />, "Einstellungen", ["admin"]],
    ["users", <Users size={18} />, "Benutzer", ["admin"]],
    ["audit", <History size={18} />, "Protokoll", ["admin"]]
  ];
  const visibleTabs = tabs.filter(([, , , roles]) => roles.includes(user.role));
  useEffect(() => {
    if (!visibleTabs.some(([id]) => id === tab)) setTab("dashboard");
  }, [tab, visibleTabs]);
  const activeTab = visibleTabs.some(([id]) => id === tab) ? tab : "dashboard";
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">{product.mark}</span>
          <div>
            <strong>Sales Crawler</strong>
            <small>{product.company}</small>
          </div>
        </div>
        <nav>
          {visibleTabs.map(([id, icon, label]) => (
            <button key={id} className={activeTab === id ? "active" : ""} onClick={() => setTab(id)}>
              {icon} {label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span>{user.name}</span>
          <small>{user.role}</small>
          <button onClick={() => api.post("/api/auth/logout").finally(onLogout)}>
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>
      <main className="content">
        <Suspense fallback={<FullScreen title="Modul wird geladen..." />}>
          {activeTab === "dashboard" && <Dashboard />}
          {activeTab === "leads" && <Leads onStartOutreach={(handoff) => { setOutreachHandoff(handoff); setTab("outreach"); }} user={user} />}
          {activeTab === "outreach" && <OutreachPage handoff={outreachHandoff} onOpenSettings={() => setTab("settings")} user={user} />}
          {activeTab === "crawl" && <Crawls user={user} />}
          {activeTab === "settings" && <SettingsPage />}
          {activeTab === "users" && <UsersPage />}
          {activeTab === "audit" && <AuditPage />}
        </Suspense>
      </main>
      <HelpBot activeTab={activeTab} user={user} />
    </div>
  );
}

function HelpBot({ activeTab, user }: { activeTab: Tab; user: SessionUser }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<HelpMessage[]>([
    {
      role: "assistant",
      content: "Ich helfe dir mit Firmensuche, Opportunity Scores, Website-Signalen, Review, Outreach und HubSpot. Frag zum Beispiel, warum ein Unternehmen als Zielkunde bewertet wurde."
    }
  ]);
  const send = () => {
    const question = input.trim();
    if (!question || busy) return;
    const nextMessages = [...messages, { role: "user" as const, content: question }];
    setMessages(nextMessages);
    setInput("");
    setBusy(true);
    api.post<{ answer: string }>("/api/help/chat", {
      message: question,
      activeTab,
      history: nextMessages.slice(-8)
    })
      .then((result) => setMessages((current) => [...current, { role: "assistant", content: result.answer }]))
      .catch((error) => setMessages((current) => [...current, { role: "assistant", content: `Ich konnte die Frage gerade nicht beantworten: ${error.message}` }]))
      .finally(() => setBusy(false));
  };
  const askPreset = (question: string) => {
    setInput(question);
    setOpen(true);
  };
  return (
    <div className="helpbot">
      {open && (
        <section className="helpbot-panel">
          <header>
            <div>
              <strong>System-Hilfe</strong>
              <small>{user.name} · {activeTab}</small>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Hilfe schließen"><X size={18} /></button>
          </header>
          <div className="helpbot-presets">
            <button type="button" onClick={() => askPreset("Warum fehlen Unternehmen in einem regionalen Crawl?")}>Crawl-Abdeckung</button>
            <button type="button" onClick={() => askPreset("Wie filtere ich Zielkunden mit Zeiterfassungs-, Einsatzplanungs- oder Nachweisbedarf?")}>Filter</button>
            <button type="button" onClick={() => askPreset("Wann wird ein Lead an HubSpot übertragen?")}>HubSpot</button>
          </div>
          <div className="helpbot-messages">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`helpbot-message ${message.role}`}>
                <pre>{message.content}</pre>
              </div>
            ))}
            {busy && <div className="helpbot-message assistant"><pre>Denke nach...</pre></div>}
          </div>
          <div className="helpbot-input">
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
              placeholder="Frage zum System stellen..."
            />
            <button type="button" className="primary" onClick={send} disabled={busy || !input.trim()}><Send size={16} /></button>
          </div>
        </section>
      )}
      <button type="button" className="helpbot-toggle" onClick={() => setOpen((value) => !value)} aria-label="System-Hilfe öffnen">
        <Bot size={22} />
      </button>
    </div>
  );
}
